import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useProductCardInview from '@/tracks/hooks/useProductCardImpresion';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { isMobile } from '@/utils/common/device';
import { buildProductDetailUrl, buildUrlWithOrigin, getPdpAdsParams } from '@/utils/url';
import { isSayWeeeApp } from '@/utils/weeeapp';
import { twMerge } from 'tailwind-merge';
import { showLoading } from '../Indicator/pageIndicator';
import type { ProductCardProps } from './types';
import { getPageKey } from '@/tracks/getPageKey';

const ProductContainer: React.FC<Partial<ProductCardProps & { interactiveSlot?: React.ReactNode }>> = props => {
  const {
    data,
    isLink = true,
    link,
    skeleton,
    skeletonFactor = false,
    className,
    addCartParams,
    children,
    transformLink,
    isInUpsell = false,
    miniTitle = false,
    cardSize,
    linkParams,
    showLoadingWhenJump,
    forceReload,
    orientation = 'v',
    interactiveSlot
  } = props;
  const { lang } = useTranslation('common');
  const router = useRouter();
  const adsCreative = data?.ads_creative || undefined;
  // 从 positionInfoT2 中获取 mod_nm
  const modNm = props?.positionInfoT2?.modSecPos?.mod_nm;

  const { ref, clickOption, impressionOption } = useProductCardInview(props);
  const { productClick } = useClickActionT2(adsCreative);

  // 仅在客户端挂载后检测 App 环境，避免 SSR/首屏无 UA 时 refPageCtx 漏拼（如 ProductList 静态数据首渲在服务端）
  const [isAppDetected, setIsAppDetected] = useState(false);

  useEffect(() => {
    if (isSayWeeeApp()) {
      setIsAppDetected(true);
    }
  }, []);

  // Weee App 下将 mod_nm 作为 refPageCtx 编码后拼到 URL
  const refPageCtxParam =
    isAppDetected && modNm ? { refPageCtx: encodeURIComponent(JSON.stringify({ mod_nm: modNm })) } : {};
  const appendRefPageCtx = (url: string) => {
    if (refPageCtxParam.refPageCtx) {
      return (url.includes('?') ? `${url}&` : `${url}?`) + `refPageCtx=${refPageCtxParam.refPageCtx}`;
    }
    return url;
  };

  const productPathSegment = data?.slug ? data?.slug : `weee/${data?.id}`;
  const linkParamsKey = JSON.stringify(linkParams);

  const productLink = useMemo(() => {
    let reload = false;
    if (isSayWeeeApp() || !isMobile()) {
      reload = true;
    }
    /**
     * 如果是广告商品，跳转链接使用trackRedirectUrl
     */
    if (data.is_sponsored && data.ads_creative) {
      const adsUrlParams = getPdpAdsParams(data);
      return {
        to: buildProductDetailUrl({
          lang,
          productPathSegment,
          linkParams,
          refPageCtxParam,
          extraParams: adsUrlParams as Record<string, string | number>
        }),
        reload: reload
      };
    }
    // 强制跳转刷新
    if (forceReload) {
      return {
        to: buildProductDetailUrl({ lang, productPathSegment, linkParams, refPageCtxParam }),
        reload: true
      };
    }
    if (link) {
      return {
        to: appendRefPageCtx(link),
        reload: reload
      };
    }
    if (isInUpsell) {
      return {
        to: 'upsell',
        reload: false
      };
    }
    //bundle
    if (data?.category === 'bundle') {
      return {
        to: buildUrlWithOrigin(`/${lang}/bundle_buy/product?product_id=${data?.id}`),
        reload: true
      };
    }
    if (transformLink) {
      const _transformLink = transformLink(data);
      //处理transformLink返回的是对象的情况
      if (typeof _transformLink === 'object') {
        return {
          to: appendRefPageCtx(_transformLink.to),
          reload: _transformLink.reload
        };
      }
      return {
        to: appendRefPageCtx(_transformLink),
        reload: reload
      };
    }
    return {
      to: buildProductDetailUrl({ lang, productPathSegment, linkParams, refPageCtxParam }),
      reload: reload
    };
  }, [
    data,
    addCartParams?.source,
    lang,
    productPathSegment,
    linkParamsKey,
    isInUpsell,
    refPageCtxParam,
    forceReload,
    link,
    transformLink,
    appendRefPageCtx,
    isAppDetected
  ]);

  const pageKey = useMemo(() => {
    if (typeof window === 'undefined') return ''; // SSR时返回空值
    return getPageKey(router.asPath);
  }, [router.asPath]);

  const cls = twMerge(
    classNames('w-full flex flex-col justify-between relative bg-surface-100-bg overflow-hidden rounded-400', {
      'cursor-pointer': isLink,
      [className]: !!className,
      'h-full': miniTitle,
      'w-[150px]': cardSize === 'sm'
    })
  );

  const trackClick = useCallback(() => {
    if (showLoadingWhenJump) {
      showLoading();
    }
    productClick(clickOption);
    props?.afterTrackClick?.();
  }, [impressionOption, clickOption]);

  if (skeleton && skeletonFactor) {
    return <>{skeleton}</>;
  }

  if (productLink.to === 'upsell' || !isLink) {
    return (
      <div
        data-o={orientation}
        data-testid="wid-product-card-container"
        data-product-id={data?.id}
        data-trace-id={data?.recommend_trace_id || data?.recommendation_trace_id}
        data-mod-nm={modNm}
        data-page-key={pageKey ?? ''}
        className={cls}
        ref={ref}>
        {children}
        {/* 处理ADA问题，a标签中不应嵌套button等可交互元素 */}
        {interactiveSlot}
      </div>
    );
  }

  if (productLink.reload) {
    return (
      <div
        data-o={orientation}
        data-testid="wid-product-card-container"
        data-product-id={data?.id}
        data-trace-id={data?.recommend_trace_id || data?.recommendation_trace_id}
        data-mod-nm={modNm}
        data-page-key={pageKey ?? ''}
        className={cls}
        ref={ref}
        onClick={trackClick}>
        <a href={productLink.to} aria-label={`${data?.name}`} className="relative block w-full h-full">
          {children}
        </a>
        {/* 处理ADA问题，a标签中不应嵌套button等可交互元素 */}
        {interactiveSlot}
      </div>
    );
  }

  return (
    <div
      data-o={orientation}
      data-testid="wid-product-card-container"
      data-product-id={data?.id}
      data-trace-id={data?.recommend_trace_id || data?.recommendation_trace_id}
      data-mod-nm={modNm}
      data-page-key={pageKey ?? ''}
      className={cls}
      ref={ref}
      onClick={trackClick}>
      <Link prefetch={false} href={productLink.to}>
        <a aria-label={`weee ${data?.name} ${data?.id}`} className="relative block w-full h-full">
          {children}
        </a>
      </Link>
      {/* 处理ADA问题，a标签中不应嵌套button等可交互元素 */}
      {interactiveSlot}
    </div>
  );
};

export default ProductContainer;
